import React, { useEffect, useRef, useState } from 'react';

// Small icon button that copies `text` to the clipboard and briefly shows a
// checkmark to confirm. Falls back to execCommand for non-secure contexts
// (e.g. plain-http dev over the LAN) where navigator.clipboard is unavailable.
const copyText = async (text) => {
	if (navigator.clipboard?.writeText) {
		try {
			await navigator.clipboard.writeText(text);
			return true;
		} catch {
			// fall through to legacy path
		}
	}
	try {
		const ta = document.createElement('textarea');
		ta.value = text;
		ta.setAttribute('readonly', '');
		ta.style.position = 'fixed';
		ta.style.opacity = '0';
		document.body.appendChild(ta);
		ta.select();
		const ok = document.execCommand('copy');
		document.body.removeChild(ta);
		return ok;
	} catch {
		return false;
	}
};

const CopyButton = ({ text, label = 'Copy', className = '', size = 14 }) => {
	const [state, setState] = useState('idle'); // idle | copied | failed
	const timer = useRef(null);

	useEffect(() => () => clearTimeout(timer.current), []);

	const handleClick = async (e) => {
		e.preventDefault();
		e.stopPropagation();
		const ok = await copyText(text);
		setState(ok ? 'copied' : 'failed');
		clearTimeout(timer.current);
		timer.current = setTimeout(() => setState('idle'), 1500);
	};

	const title = state === 'copied' ? 'Copied!' : state === 'failed' ? 'Copy failed' : label;
	const color =
		state === 'copied'
			? 'text-green-600'
			: state === 'failed'
				? 'text-red-500'
				: 'text-gray-400 hover:text-blue-600';

	return (
		<button
			type='button'
			onClick={handleClick}
			title={title}
			aria-label={title}
			className={`inline-flex items-center justify-center rounded p-0.5 transition-colors ${color} ${className}`}
		>
			{state === 'copied' ? (
				<svg width={size} height={size} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
					<polyline points='20 6 9 17 4 12' />
				</svg>
			) : (
				<svg width={size} height={size} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
					<rect x='9' y='9' width='13' height='13' rx='2' ry='2' />
					<path d='M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1' />
				</svg>
			)}
		</button>
	);
};

export default CopyButton;
